/**
 * Central rules for when an API error should wipe the SPA auth session (localStorage + Pinia).
 * Bearer Sanctum tokens are not invalidated by 419 CSRF or random 401s on non-session routes.
 */

/** Normalize axios `config.url` (may be `auth/me` or full URL with `/api/`). */
export function normalizeApiRelativePath(url: string | undefined): string {
  if (!url) return ''
  let s = url.split('?')[0]
  if (s.startsWith('http')) {
    try {
      s = new URL(s).pathname
    } catch {
      return ''
    }
  }
  const idx = s.indexOf('/api/')
  if (idx !== -1) {
    s = s.slice(idx + 5)
  }
  return s.replace(/^\/+/, '')
}

export function isAuthMeRequestUrl(url: string | undefined): boolean {
  const s = normalizeApiRelativePath(url)
  return s === 'auth/me' || s.endsWith('/auth/me')
}

export function isAuthRefreshRequestUrl(url: string | undefined): boolean {
  const s = normalizeApiRelativePath(url)
  return s === 'auth/refresh' || s.endsWith('/auth/refresh')
}

export function isAuthLogoutRequestUrl(url: string | undefined): boolean {
  const s = normalizeApiRelativePath(url)
  return s === 'auth/logout' || s.endsWith('/auth/logout')
}

/** True when 401 proves (or should treat as) invalid PAT / logout — not arbitrary resource 401. */
export function shouldClearClientSessionOnAxios401(url: string | undefined): boolean {
  return isAuthMeRequestUrl(url) || isAuthRefreshRequestUrl(url) || isAuthLogoutRequestUrl(url)
}

/**
 * Whether the global axios error handler should clear local auth and run hard-logout callback.
 * Never for 419 (Bearer SPA); only selective 401 per {@link shouldClearClientSessionOnAxios401}.
 */
export function shouldInvalidateClientSessionOnAxiosError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false
  }
  const ax = error as { response?: { status?: number }; config?: { url?: string } }
  const status = ax.response?.status
  if (status === 419) {
    return false
  }
  if (status !== 401) {
    return false
  }
  return shouldClearClientSessionOnAxios401(ax.config?.url)
}

export type AuthErrorPolicyAction = 'hard_logout' | 'reject_only'

export function classifyAxiosAuthInterceptorAction(error: unknown): AuthErrorPolicyAction {
  if (shouldInvalidateClientSessionOnAxiosError(error)) {
    return 'hard_logout'
  }
  const ax = error as { response?: { status?: number } }
  if (ax.response?.status === 419) {
    return 'reject_only'
  }
  return 'reject_only'
}
